<template>
  <section class="visit-stats" :class="{ 'visit-stats--plain': plain }" :aria-labelledby="plain ? undefined : 'visit-stats-title'">
    <header v-if="!plain" class="visit-stats__head">
      <h2 id="visit-stats-title" class="visit-stats__title">방문자 통계</h2>
    </header>

    <p v-if="loading" class="visit-stats__state">불러오는 중입니다.</p>
    <p v-else-if="error" class="visit-stats__state visit-stats__state--error">{{ error }}</p>

    <template v-else>
      <dl class="visit-stats__summary">
        <div class="visit-stats__row visit-stats__row--today">
          <dt>오늘</dt>
          <dd>{{ formatCount(stats.todayVisitors) }}<em>명</em></dd>
        </div>
        <div class="visit-stats__row">
          <dt>어제</dt>
          <dd>{{ formatCount(stats.yesterdayVisitors) }}<em>명</em></dd>
        </div>
        <div class="visit-stats__row">
          <dt>누적</dt>
          <dd>{{ formatCount(stats.totalVisitors) }}<em>명</em></dd>
        </div>
      </dl>

      <p class="visit-stats__pageviews">
        오늘 페이지뷰 <strong>{{ formatCount(stats.todayPageviews) }}</strong>
      </p>

      <div class="visit-stats__chart" aria-label="최근 7일 방문자">
        <p class="visit-stats__chart-label">최근 7일</p>
        <ul class="visit-stats__bars">
          <li v-for="day in stats.last7days" :key="day.date">
            <span
              class="visit-stats__bar"
              :style="{ height: barHeight(day.visitors) }"
              :title="`${formatDateLabel(day.date)} ${formatCount(day.visitors)}명`"
            />
            <span class="visit-stats__bar-label">{{ weekdayLabel(day.date) }}</span>
          </li>
        </ul>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { fetchAdminVisitStats, type PortalVisitStats } from '@/api/portalVisit'

defineProps<{
  plain?: boolean
}>()

const emptyStats = (): PortalVisitStats => ({
  todayVisitors: 0,
  todayPageviews: 0,
  yesterdayVisitors: 0,
  yesterdayPageviews: 0,
  totalVisitors: 0,
  totalPageviews: 0,
  last7days: [],
  todaySources: [],
  yesterdaySources: [],
  totalSources: [],
})

const loading = ref(true)
const error = ref('')
const stats = ref<PortalVisitStats>(emptyStats())

const formatCount = (value: number) => Number(value || 0).toLocaleString('ko-KR')

const weekdayLabel = (ymd: string) => {
  const [year, month, day] = ymd.split('-').map(Number)
  if (!year || !month || !day) return ''
  return ['일', '월', '화', '수', '목', '금', '토'][new Date(year, month - 1, day).getDay()]
}

const formatDateLabel = (ymd: string) => {
  const [year, month, day] = ymd.split('-')
  if (!year || !month || !day) return ymd
  return `${month}.${day}`
}

const barHeight = (visitors: number) => {
  const max = Math.max(1, ...stats.value.last7days.map((day) => day.visitors))
  const ratio = Math.max(0.08, visitors / max)
  return `${Math.round(ratio * 56)}px`
}

onMounted(async () => {
  try {
    stats.value = await fetchAdminVisitStats()
  } catch (err) {
    error.value = err instanceof Error ? err.message : '방문자 통계를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" src="./VisitorStatsWidget.scss"></style>
